import { Route } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import type { RouterHistory } from '@sentry/react/types/reactrouter';
import type { Event } from '@sentry/types';
import Cookies from 'js-cookie';

import { DEPLOYMENT_ENVIRONMENT, SENTRY_URL } from '@config/client.config';
import { APP_VERSION } from '@config/common.config';

import { getLocaleFromPathname } from './locale';
import {
  getLocalizationCountryCookie,
  SHOP_STORE_CODE_COOKIE,
} from './localization';

export const SentryRoute = Sentry.withSentryRouting(Route);
const NO_DATA = 'unknown';

export function initSentry({ history }: { history: RouterHistory }): void {
  Sentry.init({
    dsn: SENTRY_URL,
    environment: DEPLOYMENT_ENVIRONMENT,
    ignoreErrors: ['AbortError'],
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
      }),
    ],
    tracesSampleRate: 1.0,
  });

  Sentry.configureScope((scope) => {
    scope.setTag('app_version', APP_VERSION);
    scope.setTag('app_namespace', window.location.pathname.split('/')[2]);
    scope.setTag(
      'app_language',
      getLocaleFromPathname(window.location.pathname),
    );
    scope.setTag('app_country', getLocalizationCountryCookie() ?? NO_DATA);
    scope.setTag(
      'app_store_code',
      Cookies.get(SHOP_STORE_CODE_COOKIE) ?? NO_DATA,
    );
    scope.setTag('error_type', 'genericError');
  });
}

export enum ClientErrorType {
  GENERIC = 'generic',
  FATAL = 'fatal',
  GRAPHQL = 'graphql',
}

class CustomError extends Error {
  public constructor({ name, message, stack }) {
    super(message);
    this.name = name;
    if (stack) {
      this.stack = stack;
    }
  }
}

interface TagInterface {
  name: string;
  value: string;
}

interface CaptureErrorInterface {
  name: Error['name'];
  message: Error['message'];
  stack?: Error['stack'];
  tags?: TagInterface[];
  extra?: Event['extra'];
}

export default ({
  name,
  message,
  stack,
  tags,
  extra,
}: CaptureErrorInterface): void => {
  Sentry?.withScope((scope) => {
    const error = new CustomError({
      name,
      message,
      stack,
    });

    if (tags) {
      tags.forEach(({ name: tagName, value }) => scope.setTag(tagName, value));
    }
    if (extra) {
      scope.setExtra('additionalData', extra);
    }

    Sentry.captureException(error);
  });
};
